import { Link } from "react-router-dom";

/**
 * Setup guide: Garmin → Apple Health → HAE → Haus.
 *
 * Linked from /integrations Garmin card. Replaces the previous link that
 * opened the source repo on GitHub.
 */
export function DocsGarminSetup() {
  return (
    <article
      aria-labelledby="docs-garmin-heading"
      style={{ maxWidth: 720 }}
    >
      <p
        style={{
          margin: 0,
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
        }}
      >
        <Link to="/integrations" style={{ color: "var(--fg-3)" }}>
          Integrations
        </Link>{" "}
        · Garmin via HAE
      </p>
      <h1
        id="docs-garmin-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-6)",
        }}
      >
        Connect Garmin to Haus.
      </h1>

      <ol style={{ display: "grid", gap: "var(--space-5)", paddingLeft: 0, listStyle: "none" }}>
        <Step n={1} title="Pair Garmin with Apple Health">
          <p>
            On your iPhone, open the <strong>Garmin Connect</strong> app, then{" "}
            <em>More → Settings → Health Stats → Apple Health</em>. Toggle every
            metric Haus uses: HRV, resting heart rate, sleep, steps, floors
            climbed, active and basal energy, exercise minutes, blood oxygen
            saturation, respiratory rate.
          </p>
        </Step>

        <Step n={2} title="Install Health Auto Export">
          <p>
            Install the{" "}
            <a
              href="https://www.healthexportapp.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--accent)" }}
            >
              Health Auto Export
            </a>{" "}
            iOS app (one-time purchase). HAE reads the same Apple Health store
            Garmin Connect just populated and forwards the data on a cadence
            you choose.
          </p>
        </Step>

        <Step n={3} title="Configure HAE to send to your Haus Worker">
          <p>
            In HAE: <em>Automations → Add Automation → REST API</em>. Settings:
          </p>
          <ul style={{ paddingLeft: "var(--space-5)", display: "grid", gap: 4 }}>
            <li>
              URL:{" "}
              <code className="mono" style={codeStyle()}>
                https://haus-worker.jonasrackl.workers.dev/api/hae
              </code>
            </li>
            <li>Method: POST</li>
            <li>
              Header:{" "}
              <code className="mono" style={codeStyle()}>
                x-hae-token: &lt;your shared token&gt;
              </code>{" "}
              (request from <Link to="/settings">/settings</Link> if you do not
              have one)
            </li>
            <li>Format: Aggregate · Daily</li>
            <li>Frequency: every 1h or "on demand"</li>
            <li>Categories to send: every checkbox under HRV, Heart, Sleep, Activity, Body</li>
          </ul>
        </Step>

        <Step n={4} title="Trigger a manual export to verify">
          <p>
            HAE → <em>Automations → Haus → Manual Export</em>. The response
            should read{" "}
            <code className="mono" style={codeStyle()}>
              {"{ ok: true, imported: N, failed: 0 }"}
            </code>
            . Within 30 seconds, the Today vitals strip on{" "}
            <Link to="/" style={{ color: "var(--accent)" }}>
              haus.jonasrackl.com
            </Link>{" "}
            should populate.
          </p>
        </Step>
      </ol>

      <p
        style={{
          marginTop: "var(--space-6)",
          padding: "var(--space-4)",
          background: "var(--bg-3)",
          borderRadius: "var(--radius-card)",
          fontSize: "var(--type-body-s)",
          color: "var(--fg-2)",
          lineHeight: 1.5,
        }}
      >
        Troubleshooting: if the vitals strip stays blank after a Manual Export,
        check that all the HealthKit permissions Garmin Connect requested are
        green (Settings → Apps → Garmin Connect → Health). HAE only sees what
        Apple Health sees.
      </p>
    </article>
  );
}

function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li
      style={{
        display: "grid",
        gridTemplateColumns: "32px 1fr",
        gap: "var(--space-4)",
      }}
    >
      <span
        className="mono"
        style={{
          width: 28,
          height: 28,
          background: "var(--accent-wash)",
          color: "var(--accent)",
          borderRadius: "50%",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "var(--type-body-s)",
          fontWeight: 500,
        }}
      >
        {n}
      </span>
      <div style={{ display: "grid", gap: "var(--space-2)" }}>
        <h2
          style={{
            margin: 0,
            fontSize: "var(--type-body-l)",
            fontWeight: 500,
          }}
        >
          {title}
        </h2>
        <div
          style={{
            color: "var(--fg-2)",
            fontSize: "var(--type-body)",
            lineHeight: 1.6,
          }}
        >
          {children}
        </div>
      </div>
    </li>
  );
}

function codeStyle(): React.CSSProperties {
  return {
    background: "var(--bg-3)",
    border: "1px solid var(--hair-2)",
    padding: "1px 6px",
    borderRadius: 4,
    fontSize: "var(--type-meta)",
  };
}
